import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, GripVertical } from "lucide-react";
import { api, resolveImage, formatApiErrorDetail } from "@/lib/api";
import ImageUpload from "@/components/admin/ImageUpload";

const EMPTY = { title: "", description: "", image_url: "", ingredients: [""], steps: [""], published: true, sort_order: 0 };
const field = "w-full rounded-xl border border-[#1A1A1A]/15 px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#8B5CF6]/40 bg-white";

const RecipesManager = () => {
  const [recipes, setRecipes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/recipes?all=true").then((r) => setRecipes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...EMPTY, ingredients: [""], steps: [""], sort_order: recipes.length + 1 });
  const openEdit = (r) => setEditing({ ...r, ingredients: r.ingredients?.length ? [...r.ingredients] : [""], steps: r.steps?.length ? [...r.steps] : [""] });

  const updateRow = (key, i, val) => setEditing((p) => ({ ...p, [key]: p[key].map((x, idx) => (idx === i ? val : x)) }));
  const addRow = (key) => setEditing((p) => ({ ...p, [key]: [...p[key], ""] }));
  const removeRow = (key, i) => setEditing((p) => ({ ...p, [key]: p[key].filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...editing,
      ingredients: editing.ingredients.map((s) => s.trim()).filter(Boolean),
      steps: editing.steps.map((s) => s.trim()).filter(Boolean),
      sort_order: Number(editing.sort_order) || 0,
    };
    delete payload.id;
    delete payload.created_at;
    try {
      if (editing.id) {
        await api.put(`/recipes/${editing.id}`, payload);
        toast.success("Recipe updated");
      } else {
        await api.post("/recipes", payload);
        toast.success("Recipe added");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this recipe?")) return;
    await api.delete(`/recipes/${id}`);
    toast.success("Recipe deleted");
    load();
  };

  const RowEditor = ({ label, keyName, numbered }) => (
    <div>
      <label className="text-sm font-semibold text-[#1A1A1A]/70">{label}</label>
      <div className="mt-2 space-y-2">
        {editing[keyName].map((val, i) => (
          <div key={i} className="flex items-center gap-2">
            {numbered && <span className="w-6 text-center font-bold text-[#8B5CF6] text-sm">{i + 1}</span>}
            {!numbered && <GripVertical className="w-4 h-4 text-[#1A1A1A]/25" />}
            <input className={field} value={val} onChange={(e) => updateRow(keyName, i, e.target.value)} data-testid={`recipe-${keyName}-${i}`} />
            <button type="button" onClick={() => removeRow(keyName, i)} className="p-2 rounded-lg text-[#FF3D68] hover:bg-[#FF3D68]/10"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button type="button" onClick={() => addRow(keyName)} className="text-sm font-bold text-[#8B5CF6] inline-flex items-center gap-1" data-testid={`add-${keyName}-row`}>
          <Plus className="w-4 h-4" /> Add {numbered ? "step" : "ingredient"}
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="recipes-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-extrabold text-2xl text-[#1A1A1A]">Recipes ({recipes.length})</h2>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-[#8B5CF6] hover:opacity-90 text-white font-bold text-sm px-5 py-2.5 transition" data-testid="add-recipe-btn">
          <Plus className="w-4 h-4" /> Add Recipe
        </button>
      </div>

      <div className="grid gap-3">
        {recipes.map((r) => (
          <div key={r.id} className="flex items-center gap-4 bg-white rounded-2xl border border-[#1A1A1A]/5 p-3 shadow-sm" data-testid={`recipe-row-${r.id}`}>
            <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#FFF8F0] flex-shrink-0">
              {r.image_url && <img src={resolveImage(r.image_url)} alt={r.title} className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-display font-bold text-[#1A1A1A] truncate">{r.title}</p>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${r.published ? "bg-[#8B5CF6]/15 text-[#8B5CF6]" : "bg-[#1A1A1A]/10 text-[#1A1A1A]/50"}`}>
                  {r.published ? "Published" : "Draft"}
                </span>
              </div>
              <p className="text-sm text-[#1A1A1A]/50 truncate">{r.ingredients?.length || 0} ingredients · {r.steps?.length || 0} steps</p>
            </div>
            <button onClick={() => openEdit(r)} className="p-2 rounded-lg hover:bg-[#FFF8F0] text-[#1A1A1A]/60" data-testid={`edit-recipe-${r.id}`}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(r.id)} className="p-2 rounded-lg hover:bg-[#FF3D68]/10 text-[#FF3D68]" data-testid={`delete-recipe-${r.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" data-testid="recipe-form-modal">
          <div className="absolute inset-0 bg-[#1A1A1A]/50 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <form onSubmit={save} className="relative bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 sm:p-8 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-extrabold text-xl">{editing.id ? "Edit Recipe" : "New Recipe"}</h3>
              <button type="button" onClick={() => setEditing(null)} className="p-1.5 rounded-full hover:bg-[#FFF8F0]"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-[#1A1A1A]/70">Photo</label>
                <div className="mt-2"><ImageUpload value={editing.image_url} onChange={(url) => setEditing((p) => ({ ...p, image_url: url }))} testId="recipe-image" /></div>
              </div>
              <div>
                <label className="text-sm font-semibold text-[#1A1A1A]/70">Title</label>
                <input className={field} value={editing.title} onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))} required data-testid="recipe-title-input" />
              </div>
              <div>
                <label className="text-sm font-semibold text-[#1A1A1A]/70">Short description</label>
                <textarea className={field} rows={2} value={editing.description} onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))} data-testid="recipe-description-input" />
              </div>
              <RowEditor label="Ingredients" keyName="ingredients" numbered={false} />
              <RowEditor label="Steps" keyName="steps" numbered={true} />
              <div className="flex items-center justify-between bg-[#FFF8F0] rounded-xl px-4 py-3">
                <span className="text-sm font-semibold text-[#1A1A1A]/70">Published</span>
                <button type="button" onClick={() => setEditing((p) => ({ ...p, published: !p.published }))} className={`w-12 h-7 rounded-full transition-colors relative ${editing.published ? "bg-[#8B5CF6]" : "bg-[#1A1A1A]/20"}`} data-testid="recipe-published-toggle">
                  <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${editing.published ? "left-6" : "left-1"}`} />
                </button>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setEditing(null)} className="flex-1 rounded-full border border-[#1A1A1A]/15 font-bold py-3 hover:bg-[#FFF8F0]">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 rounded-full bg-[#8B5CF6] hover:opacity-90 text-white font-bold py-3 transition disabled:opacity-60" data-testid="save-recipe-btn">
                {saving ? "Saving…" : "Save Recipe"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default RecipesManager;
